import { Button, Input, Surface, Textarea } from '@chopchop-se/ui';
import { useMutation } from '@tanstack/react-query';
import {
  CLAIM_COLD_CHAIN_REQUIRED_PHOTOS,
  CLAIM_CREATE_REASONS,
  type ClaimCreateReason,
  type ClaimPhotoCategory,
  type ClaimableOrder,
} from '@warehouse/shared';
import { Search, Send, Trash2 } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { PageHeader } from '../../../components/PageHeader';
import { formatDate, formatQty } from '../../../lib/format';
import { errorMessage, toastError, toastSuccess } from '../../../lib/notify';
import { createClaim, fetchClaimableOrder, stageClaimPhoto } from '../api/claims-api';

/**
 * Filing a claim, in either chrome.
 *
 * The form is ORDER-FIRST: the restaurant reads the order number off the
 * packing slip (or arrives from a deep link with `?order=`), the server returns
 * the claimable lines, and the quantities are entered per line — which is what
 * makes "3 kg of the chicken was spoiled" a fact the statistics can count.
 *
 * Cold-chain claims need a signed freight note AND a damaged-goods photo. The
 * hint and the disabled submit are here; the RULE is server-side.
 */
export function ClaimFormPage({ portal = false }: { portal?: boolean }): ReactNode {
  const { t } = useTranslation(['claims', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [orderQuery, setOrderQuery] = useState(searchParams.get('order') ?? '');
  const [order, setOrder] = useState<ClaimableOrder | null>(null);
  const [reason, setReason] = useState<ClaimCreateReason>('missing');
  const [description, setDescription] = useState('');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<{ fileId: string; name: string; category: ClaimPhotoCategory }[]>([]);
  const [photoCategory, setPhotoCategory] = useState<ClaimPhotoCategory>('damaged_goods');
  const [formError, setFormError] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: (identifier: string) => fetchClaimableOrder(identifier.trim()),
    onSuccess: (data) => {
      setOrder(data);
      setQuantities({});
      setFormError(null);
    },
    onError: (error) => {
      setOrder(null);
      setFormError(errorMessage(error));
    },
  });

  const upload = useMutation({
    mutationFn: (input: { file: File; category: ClaimPhotoCategory }) => stageClaimPhoto(input),
    onSuccess: (data, variables) => {
      setPhotos((current) => [...current, { fileId: data.fileId, name: data.name, category: variables.category }]);
      toastSuccess(t('claims:photo.uploaded'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      if (!order) throw new Error(t('claims:form.validation.restaurantRequired'));
      const lines = Object.entries(quantities)
        .filter(([, qty]) => Number(qty) > 0)
        .map(([articleId, qty]) => ({ articleId, qtyBase: qty }));
      if (lines.length === 0) throw new Error(t('claims:form.validation.linesRequired'));
      if (reason === 'other' && description.trim().length === 0) {
        throw new Error(t('claims:form.validation.descriptionRequired'));
      }

      return createClaim({
        restaurantId: order.restaurantId,
        salesOrderId: order.salesOrderId,
        reason,
        description: description.trim(),
        lines,
        photos: photos.map((photo) => ({ fileId: photo.fileId, category: photo.category })),
      });
    },
    onSuccess: (claim) => {
      toastSuccess(t('claims:form.created', { claimNo: claim.claimNo }));
      void navigate(portal ? `/portal/claims/${claim.id}` : `/orders/claims/${claim.id}`);
    },
    onError: (error) => {
      setFormError(errorMessage(error));
    },
  });

  const missingColdChain =
    reason === 'cold_chain'
      ? CLAIM_COLD_CHAIN_REQUIRED_PHOTOS.filter(
          (category) => !photos.some((photo) => photo.category === category),
        )
      : [];

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    setFormError(null);
    submit.mutate();
  }

  return (
    <form onSubmit={onSubmit}>
      <PageHeader
        breadcrumb={
          <Link to={portal ? '/portal/claims' : '/orders/claims'} className="hover:text-ink">
            {portal ? t('claims:portal.title') : t('claims:title')}
          </Link>
        }
        title={t('claims:form.newTitle')}
        subtitle={t('claims:form.newSubtitle')}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Surface variant="card" className="lg:col-span-2">
          <label className="block text-sm text-ink-light" htmlFor="claim-order">
            {t('claims:form.order')}
          </label>
          <div className="mt-1 flex gap-2">
            <Input
              id="claim-order"
              className="flex-1"
              value={orderQuery}
              placeholder={t('claims:form.orderPlaceholder')}
              onChange={(event) => {
                setOrderQuery(event.target.value);
              }}
            />
            <Button
              type="button"
              variant="secondary"
              icon={<Search />}
              loading={lookup.isPending}
              disabled={orderQuery.trim().length === 0}
              onClick={() => {
                lookup.mutate(orderQuery);
              }}
            >
              {t('common:actions.search')}
            </Button>
          </div>

          {order ? (
            <>
              <p className="mt-3 text-sm text-ink-light">
                <strong className="font-mono">{order.orderNo}</strong> · {order.restaurantName} ·{' '}
                {formatDate(order.deliveryDate)}
              </p>

              <h2 className="mt-5 font-display text-base text-ink">{t('claims:form.lines')}</h2>
              <p className="text-xs text-ink-muted">{t('claims:form.linesHelp')}</p>

              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-border text-left text-xs uppercase text-ink-muted">
                      <th className="py-2 pr-3">{t('claims:form.article')}</th>
                      <th className="py-2 pr-3 text-right">{t('claims:form.ordered')}</th>
                      <th className="py-2 text-right">{t('claims:form.quantity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.lines.map((line) => (
                      <tr key={line.orderLineId} className="border-b border-surface-border/60">
                        <td className="py-2 pr-3">
                          <span className="font-mono text-xs text-ink-muted">{line.articleNo}</span>{' '}
                          {line.articleName}
                        </td>
                        <td className="py-2 pr-3 text-right text-ink-muted">
                          {formatQty(line.qtyBase, { unit: line.baseUnitCode ?? undefined })}
                        </td>
                        <td className="py-2 text-right">
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            inputMode="decimal"
                            aria-label={`${t('claims:form.quantity')} ${line.articleName}`}
                            className="w-28 rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-right text-sm"
                            value={quantities[line.articleId] ?? ''}
                            onChange={(event) => {
                              const value = event.target.value;
                              setQuantities((current) => ({ ...current, [line.articleId]: value }));
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </Surface>

        <Surface variant="card">
          <label className="block text-sm text-ink-light" htmlFor="claim-reason">
            {t('claims:form.reason')}
          </label>
          <select
            id="claim-reason"
            className="mt-1 w-full rounded-lg border border-surface-border bg-surface px-2 py-2 text-sm"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value as ClaimCreateReason);
            }}
          >
            {CLAIM_CREATE_REASONS.map((value) => (
              <option key={value} value={value}>
                {t(`claims:reason.${value}`)}
              </option>
            ))}
          </select>

          <label className="mt-4 block text-sm text-ink-light" htmlFor="claim-description">
            {t('claims:form.description')}
          </label>
          <Textarea
            id="claim-description"
            className="mt-1 w-full"
            rows={4}
            value={description}
            placeholder={t('claims:form.descriptionPlaceholder')}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
          {reason === 'other' ? (
            <p className="mt-1 text-xs text-ink-muted">{t('claims:form.descriptionHelpOther')}</p>
          ) : null}

          <h2 className="mt-5 font-display text-base text-ink">{t('claims:photo.title')}</h2>
          {reason === 'cold_chain' ? (
            <p className="mt-1 rounded-xl bg-warning/10 px-3 py-2 text-xs text-ink-light">
              {t('claims:photo.coldChainRequired')}
            </p>
          ) : null}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              aria-label={t('claims:photo.category')}
              className="rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-sm"
              value={photoCategory}
              onChange={(event) => {
                setPhotoCategory(event.target.value as ClaimPhotoCategory);
              }}
            >
              {(['freight_note', 'damaged_goods', 'other'] as ClaimPhotoCategory[]).map((value) => (
                <option key={value} value={value}>
                  {t(`claims:photo.categories.${value}`)}
                </option>
              ))}
            </select>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              aria-label={t('claims:photo.add')}
              className="text-xs"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload.mutate({ file, category: photoCategory });
                event.target.value = '';
              }}
            />
          </div>

          {photos.length > 0 ? (
            <ul className="mt-3 space-y-1 text-xs">
              {photos.map((photo) => (
                <li key={photo.fileId} className="flex items-center justify-between gap-2">
                  <span className="truncate">
                    {t(`claims:photo.categories.${photo.category}`)} · {photo.name}
                  </span>
                  <button
                    type="button"
                    aria-label={`${t('common:actions.remove')} ${photo.name}`}
                    className="text-ink-muted hover:text-danger"
                    onClick={() => {
                      setPhotos((current) => current.filter((p) => p.fileId !== photo.fileId));
                    }}
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {missingColdChain.length > 0 ? (
            <p className="mt-3 text-xs text-danger" role="alert">
              {t('claims:photo.coldChainMissing')}
            </p>
          ) : null}

          {formError ? (
            <p className="mt-3 text-sm text-danger" role="alert">
              {formError}
            </p>
          ) : null}

          <Button
            type="submit"
            className="mt-4 w-full"
            icon={<Send />}
            loading={submit.isPending}
            disabled={!order || missingColdChain.length > 0}
          >
            {t('claims:form.submit')}
          </Button>
        </Surface>
      </div>
    </form>
  );
}
